import moment from 'moment';
import CryptoJS from 'crypto-js';
import DOMPurify from 'dompurify';
import { parseObj } from './commonUtils';
import { notificationManager } from './NotificationUtils';
import { isAllowedExternalUrl } from './urlValidationUtils';

export const handleValidation = (currField, updateErrors = true) => {
  let errorFound = false;
  const fieldValue =
    currField.value && typeof currField.value === 'string'
      ? currField.value.trim()
      : currField.value;

  if (!currField.validations || currField.validations.length === 0) {
    // Even optional fields must reject HTML tags
    if (typeof fieldValue === 'string' && !currField.allowHtml && isHTMLIncluded(fieldValue)) {
      if (updateErrors) {
        currField.errors = currField.errors || {};
        currField.errors['htmlError'] = currField.fieldName + ' must not contain HTML tags';
      }
      return true;
    }
    return false;
  }

  // check required fields
  if (currField.validations.includes('required')) {
    let requiredError = null;
    if ((!fieldValue || fieldValue.length === 0) && typeof currField?.value !== 'number') {
      requiredError = currField.fieldName + ' is required';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['required'] = requiredError;
    }
  }

  // Check Password.
  if (currField?.validations?.includes('validPassword')) {
    try {
      let pwdError = validatePassword(fieldValue);

      if (pwdError) {
        errorFound = true;
      }

      if (updateErrors) {
        currField.errors['validPassword'] = pwdError;
      }
    } catch (error) {
      notificationManager('danger', 'Error when validating password.');
    }
  }

  // check maxLength
  if (currField.validations.includes('maxLength')) {
    let maxLengthError = null;
    if (fieldValue && fieldValue.length > currField.maxLength) {
      maxLengthError = currField.fieldName + ' maximum length is ' + currField.maxLength;
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['maxLength'] = maxLengthError;
    }
  }

  // check positiveOnly
  if (currField.validations.includes('positiveOnly')) {
    let positiveOnlyError = null;
    if (fieldValue && fieldValue < 1) {
      positiveOnlyError = currField.fieldName + ' should be a positive value';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['positiveOnly'] = positiveOnlyError;
    }
  }

  if (currField.validations.includes('validCustomMonthDates')) {
    let validCustomMonthDatesError = null;
    let regex = /^[0-9]+(,[0-9]+)*$/;
    const isValidFormat = regex.test(fieldValue);
    if (isValidFormat) {
      const tempDates = fieldValue?.split(',')?.map(Number);
      const isDuplicate = tempDates.some((element) => {
        if (tempDates.indexOf(element) !== tempDates.lastIndexOf(element)) {
          return true;
        }

        return false;
      });

      if (isDuplicate) {
        validCustomMonthDatesError = 'Date should be a unique number';
        errorFound = true;
      }
    }

    if (updateErrors) {
      currField.errors['validCustomMonthDates'] = validCustomMonthDatesError;
    }
  }
  //ui-builder padding
  if (currField.validations.includes('oneToFive')) {
    let oneToFive = null;
    if (fieldValue && fieldValue < 1) {
      oneToFive = 'Padding value should range from 1 to 5';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['oneToFive'] = oneToFive;
    }
  }

  // check positiveOnlyWithZero
  if (currField.validations.includes('positiveOnlyWithZero')) {
    let positiveOnlyWithZero = null;
    if (fieldValue && fieldValue < 0) {
      positiveOnlyWithZero = currField.fieldName + ' should be a positive value';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['positiveOnlyWithZero'] = positiveOnlyWithZero;
    }
  }

  // check minLength
  if (currField.validations.includes('minLength')) {
    let minLengthError = null;
    if (fieldValue && fieldValue.length < currField.minLength) {
      minLengthError = currField.fieldName + ' minimum length is ' + currField.minLength;
      errorFound = true; 
    }

    if (updateErrors) {
      currField.errors['minLength'] = minLengthError;
    }
  }

  //check email validation
  if (currField.validations.includes('validEmail')) {
    let emailError = null;
    if (fieldValue?.length > 0) {
      if (!validateEmail(fieldValue)) {
        emailError = 'Invalid email format';
        errorFound = true;
      }
    }
    if (updateErrors) {
      currField.errors['validEmail'] = emailError;
    }
  }

  //check email list validation
  if (currField.validations.includes('validEmailList')) {
    let emailError = null;
    if (fieldValue?.length > 0) {
      fieldValue.split(',').forEach((email) => {
        if (!validateEmail(email.trim())) {
          emailError = 'Invalid email format found in the list';
          errorFound = true;
        }
      });
    }
    if (updateErrors) {
      currField.errors['validEmailList'] = emailError;
    }
  }

  // check maxLimit of array elements
  if (currField.validations.includes('maxLimit')) {
    let maxLimitError = null;
    if (fieldValue && fieldValue.length >= currField.maxLimit) {
      maxLimitError =
        currField.fieldName + " has reached it's maximum limit of " + currField.maxLimit;
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['maxLimit'] = maxLimitError;
    }
  }

  // check phone number validation
  if (currField.validations.includes('validPhone')) {
    let phoneError = null;
    if (!validatePhone(fieldValue)) {
      phoneError = 'Invalid phone number';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validPhone'] = phoneError;
    }
  }

  // check time validation
  if (currField.validations.includes('validTime')) {
    let timeError = null;
    if (!validateTime(fieldValue)) {
      timeError = 'Invalid time';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validTime'] = timeError;
    }
  }

  // check date validation
  if (currField.validations.includes('validDate')) {
    let dateError = null;
    if (!validateDate(fieldValue)) {
      dateError = 'Invalid Date';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validDate'] = dateError;
    }
  }

  // check url validation
  if (currField.validations.includes('validUrl')) {
    let urlError = null;
    if (fieldValue && fieldValue?.type !== 'S3' && !validateUrl(fieldValue)) {
      urlError = 'Invalid url';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validUrl'] = urlError;
    }
  }

  // check if options url selected then url validation
  if (currField.validations.includes('validIfUrl')) {
    let urlError = null;
    if (
      fieldValue &&
      fieldValue.type === 'WEB_URL' &&
      fieldValue.url !== '' &&
      !validateUrl(fieldValue.url)
    ) {
      urlError = 'Invalid url';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validIfUrl'] = urlError;
    }
  }

  // check ip validation
  if (currField.validations.includes('validIP')) {
    let ipError = null;
    if (!validateIP(fieldValue)) {
      ipError = 'Invalid ip address';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validIP'] = ipError;
    }
  }

  // check unique number validation where it is taken only integer value
  if (currField?.validations?.includes(VALID_INTEGER)) {
    let integerError = null;
    if (fieldValue !== '' && fieldValue != null && !validateInteger(fieldValue)) {
      integerError = 'Decimal numbers are not allowed';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors[VALID_INTEGER] = integerError;
    }
  }

  // check alphaNumeric validation
  if (currField.validations.includes('validPureAlphaNumeric')) {
    let alphaNumericError = null;
    if (!validatePureAlphanumeric(fieldValue) && fieldValue !== '') {
      alphaNumericError = currField.fieldName + ' must be Alpha Numeric';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validPureAlphaNumeric'] = alphaNumericError;
    }
  }

  // check alphaNumeric validation
  if (currField.validations.includes('validAlphaNumeric')) {
    let alphaNumericError = null;
    if (fieldValue && !validateAlphanumeric(fieldValue)) {
      alphaNumericError =
        currField.fieldName + " can not contain special character except '-' & '_'";
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validAlphaNumeric'] = alphaNumericError;
    }
  }

  // check alphaNumeric validation and new line if user enter
  if (currField.validations.includes('validateAlphaNumericNewLine')) {
    let alphaNumericError = null;
    if (fieldValue && !validateAlphaNumericNewLine(fieldValue)) {
      alphaNumericError = currField.fieldName + ' can not contain special characters.';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validateAlphaNumericNewLine'] = alphaNumericError;
    }
  }

  // check invalid html tags — runs for ALL string fields by default unless allowHtml is set
  if (
    currField.validations.includes('htmlError') ||
    (typeof fieldValue === 'string' && !currField.allowHtml)
  ) {
    let hasHtmlError = null;
    const valueToValidate =
      typeof fieldValue === 'object' && fieldValue !== null ? fieldValue.url || '' : fieldValue;

    if (typeof valueToValidate === 'string' && isHTMLIncluded(valueToValidate)) {
      hasHtmlError = currField.fieldName + ' must not contain HTML tags';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['htmlError'] = hasHtmlError;
    }
  }

  // check validNumber
  if (currField.validations.includes('validPureNumber')) {
    let numberError = null;
    if (fieldValue && !validPureNumber(fieldValue)) {
      numberError = currField.fieldName + ' should be a valid number';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['validPureNumber'] = numberError;
    }
  }

  // Check domain safety (block internal IPs, localhost, suspicious domains)
  if (currField?.validations?.includes('safeDomain')) {
    let safeDomainError = null;
    let urlToCheck = '';

    if (currField?.value?.type !== 'S3') {
      if (currField?.value?.url && typeof currField?.value?.url === 'string') {
        urlToCheck = currField.value.url;
      } else if (typeof fieldValue === 'string' && parseObj(fieldValue)?.url) {
        urlToCheck = parseObj(fieldValue).url;
      } else if (fieldValue?.url !== undefined && fieldValue?.url !== null) {
        urlToCheck = fieldValue.url;
      } else if (fieldValue && typeof fieldValue === 'string') {
        urlToCheck = fieldValue;
      }

      if (urlToCheck && !isAllowedExternalUrl(urlToCheck)) {
        safeDomainError = 'URL points to a restricted or untrusted domain';
        errorFound = true;
      }
    }

    if (updateErrors) {
      currField.errors['safeDomain'] = safeDomainError;
    }
  }

  // Check URL Sanitization.
  if (currField?.validations?.includes('safeURL')) {
    try {
      let unSafeURLError = null;
      let safeURL = '';
      let URL = '';

      if (currField?.value?.type !== 'S3') {
        if (currField?.value?.url && typeof currField?.value?.url === 'string') {
          URL = currField.value.url;
          safeURL = DOMPurify.sanitize(currField.value.url);
        } else if (typeof fieldValue === 'string' && parseObj(fieldValue)?.url) {
          URL = parseObj(fieldValue).url;
          safeURL = DOMPurify.sanitize(parseObj(fieldValue).url);
        } else if (fieldValue?.url !== undefined && fieldValue?.url !== null) {
          URL = fieldValue.url;
          safeURL = DOMPurify.sanitize(fieldValue.url);
        } else if (fieldValue && typeof fieldValue === 'string') {
          URL = fieldValue;
          safeURL = DOMPurify.sanitize(fieldValue);
        } else {
          URL = fieldValue;
          safeURL = fieldValue;
        }

        if (safeURL !== URL) {
          unSafeURLError = 'Invalid url';
          errorFound = true;
        }
      }

      if (updateErrors) {
        currField.errors['safeURL'] = unSafeURLError;
      }
    } catch (error) {
      notificationManager('danger', 'Error when checking safe URL.');
    }
  }

  // check valide alphanumeric with special chr
  if (currField.validations.includes('validTextWithoutGreatherOrLessthen')) {
    let hasValidAlphaNumwithChar = null;
    if (fieldValue && (fieldValue.includes('>') || fieldValue.includes('<'))) {
      hasValidAlphaNumwithChar = '< or > are not allowed';
      errorFound = true;
    }

    if (updateErrors) {
      currField.errors['validTextWithoutGreatherOrLessthen'] = hasValidAlphaNumwithChar;
    }
  }

  if (currField.validations.includes('validHttpsUrl')) {
    let httpsUrl = null;
    if (fieldValue && !validHttpsUrl(fieldValue)) {
      httpsUrl = 'Invalid url';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validHttpsUrl'] = httpsUrl;
    }
  }

  if (currField.validations.includes('validImageMaxSize')) {
    let hasImageMaxSize = null;
    if (currField.imageDetails.uploadSize > currField.imageDetails.maxSize) {
      hasImageMaxSize = 'Sorry! Unable to add Image with size more than 10MB';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validImageMaxSize'] = hasImageMaxSize;
    }
  }

  if (currField.validations.includes('validResolution')) {
    let hasResolution = null;
    if (
      currField.imageDetails.uploadHeight > currField.imageDetails.height ||
      currField.imageDetails.uploadwidth > currField.imageDetails.width
    ) {
      hasResolution = 'Sorry! Unable to add Image with size more than 10MB';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['validResolution'] = hasResolution;
    }
  }

  if (currField.validations.includes('minimumTwo')) {
    let minTwoError = null;
    if (fieldValue !== '' && Number(fieldValue) < 2) {
      minTwoError = 'Minimum of 2 slots needed for an activity to recur';
      errorFound = true;
    }
    if (updateErrors) {
      currField.errors['minimumTwo'] = minTwoError;
    }
  }

  if (currField.validations.includes('durationWithinRange')) {
    let durationErr = null;

    const parentStart = moment(currField.parentStartTime, 'HH:mm');
    const parentEnd = moment(currField.parentEndTime, 'HH:mm');
    const windowMins = parentEnd.isBefore(parentStart)
      ? parentEnd.clone().add(1, 'day').diff(parentStart, 'minutes')
      : parentEnd.diff(parentStart, 'minutes');

    const slotMins = Number(currField.slotDuration || 0);
    const padMins = Number(currField.paddingDuration || 0);

    const slotOver = slotMins > windowMins;
    const padOver = padMins > windowMins;
    const totalOver = slotMins + padMins > windowMins;

    const isSlot = currField.fieldName === 'Slot Duration';
    const isPad = currField.fieldName === 'Padding Duration';

    if (isSlot && (slotOver || totalOver)) {
      durationErr = 'Slot duration exceeds total available time.';
    } else if (isPad && (padOver || totalOver)) {
      durationErr = 'Padding duration exceeds total available time.';
    } else if (totalOver) {
      durationErr = 'Slot or padding cannot exceed total available time.';
    }

    if (updateErrors) currField.errors['durationWithinRange'] = durationErr;
    if (durationErr) errorFound = true;
  }

  if (currField.validations.includes('recurrencesWithinRange')) {
    let recurrenceError = null;

    const parentStart = moment(currField.parentStartTime, 'HH:mm');
    const parentEnd = moment(currField.parentEndTime, 'HH:mm');
    const slotDuration = Number(currField.slotDuration || 0);
    const padDuration = Number(currField.paddingDuration || 0);
    const recurrences = Number(currField.value || 0);

    if (slotDuration > 0 && parentStart.isValid() && parentEnd.isValid() && recurrences > 0) {
      const totalNeeded = recurrences * (slotDuration + padDuration) - padDuration;
      const available = parentEnd.isBefore(parentStart)
        ? parentEnd.clone().add(1, 'day').diff(parentStart, 'minutes')
        : parentEnd.diff(parentStart, 'minutes');

      if (totalNeeded > available) {
        recurrenceError = 'This number of recurrences cannot fit within the selected total hours.';
      }
    }

    if (updateErrors) {
      currField.errors['recurrencesWithinRange'] = recurrenceError;
    }
    if (recurrenceError) errorFound = true;
  }

  return errorFound;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(?:(?:\[[0-9]{1,3}(?:\.[0-9]{1,3}){3}\])|(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const formDataValidate = (formData, updatedFields) => {
  let isValid = true;
  for (let field in formData) {
    const currField = { ...formData }[field];

    if (currField?.ignoreValidationOnSubmit) {
      currField.errors = {};
    } else {
      if (handleValidation(currField, updatedFields)) {
        isValid = false;
      }
    }
    if (updatedFields) {
      updatedFields[field] = currField;
    }
  }
  return isValid;
};

export const validatePhone = (phone) => {
  const re = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return re.test(phone);
};

export const validateTime = (time) => {
  return moment(time, 'HH:mm', true).isValid();
};

export const validateDate = (date) => {
  const tempDate = moment(date, 'DD-MM-YYYY', true);
  return tempDate.isValid();
};

// export const OLD_validateUrl = (url) => {
//   const re = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
//   return re.test(url);
// };

export const validateUrl = (url) => {
  const re =
    /^(?!.*[()])(?:https?|ftp):\/\/(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/;

  const domainCheck = /^(?!-)[A-Za-z0-9-]+([-.]{1}[a-z0-9]+)*\.[A-Za-z]{2,6}$/;

  if (!re?.test(url)) {
    return false;
  }

  // Extract the domain from the URL and test it against the domain regex
  const domain = url?.replace(/^(?:https?|ftp):\/\/(?:www\.)?/i, '')?.split('/')[0];
  return domainCheck?.test(domain);
};

export const validateHhMm = (time) => {
  const isValid = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
  return isValid;
};

export const validatePassword = (password) => {
  let errorMessage = null;
  const pwd = password;
  const domainNames = ['hudini', 'cms', 'elevate'];
  const isValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/.test(pwd);

  if (!isValid) {
    errorMessage =
      'Password must be at least 8 characters long and include uppercase, lowercase, digits, and special characters.';
  }

  for (const name of domainNames) {
    if (pwd?.toLowerCase().includes(name)) {
      errorMessage = 'Password must not contain the domain names.';
      break;
    }
  }

  return errorMessage;
};

export const validateIP = (ip) => {
  const re =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi;
  return re.test(ip);
};

export const validateAlphanumeric = (alphanumeric) => {
  const re = /^[0-9a-zA-Z _-]+$/;
  return alphanumeric.match(re) ? true : false;
};

export const validateAlphaNumericNewLine = (alphaNumericNewLine) => {
  const re = /^[0-9a-zA-Z.|\n -]+$/;
  return alphaNumericNewLine.match(re) ? true : false;
};

export const validatePureAlphanumeric = (alphanumeric) => {
  const re = /^[0-9a-zA-Z]+$/;
  return alphanumeric.match(re) ? true : false;
};

export const isRoomNumberValid = (roomNumber) => {
  if (roomNumber.length > 6) return false;
  let letters = /^[0-9a-zA-Z -]+$/;
  return roomNumber.match(letters) ? true : false;
};

export const isNumberValid = (number) => {
  if (number.length > 5) return false;
  let letters = /^[0-9]+$/;
  return number.match(letters) ? true : false;
};
export const isExtensionNumberValid = (extensionNumber) => {
  if (extensionNumber.length > 6) return false;
  let numbers = /^[0-9]+$/;
  return extensionNumber.match(numbers) ? true : false;
};

export const isPasswordValid = (password) => {
  if (password.length > 8) return false;
  let letters = /^[0-9a-zA-Z]+$/;
  return password.match(letters) ? true : false;
};

export const isHTMLIncluded = (str) => {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  const scriptTag = Boolean(
    new DOMParser().parseFromString(str, 'text/html').querySelector('script'),
  );
  return scriptTag || Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

export const validHttpsUrl = (url) => {
  const re = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm;
  return re.test(url);
};

export const validPureNumber = (value) => {
  return !isNaN(value) && isFinite(value);
};

export const getDaysOfWeekFromRange = (stDate, etDate) => {
  const startDate = moment(stDate, 'DD-MM-YYYY');
  const endDate = moment(etDate, 'DD-MM-YYYY');
  endDate.add(1, 'day');

  const daysOfWeek = [];

  let i = 0;

  while (i < 7 && startDate < endDate) {
    daysOfWeek.push(startDate.day());
    startDate.add(1, 'day');
    i++;
  }
  return daysOfWeek;
};

export const getCheckedIds = (list, propertyName) => {
  const checkedIds = [];
  list.forEach((item) => {
    if (item?.[propertyName]) {
      checkedIds.push(item.id);
    }
  });
  return checkedIds;
};

export const updateCheckedStatus = (filteredRows, checkedIdList) => {
  return filteredRows?.map((item) => {
    return {
      ...item,
      isChecked: checkedIdList.includes(item?.id),
    };
  });
};

export const updateSelectedModule = (dataList, setActionMenuList, setIsAllSelected) => {
  const selectedModuleCheckedData = dataList?.filter((item) => item?.isChecked);
  if (selectedModuleCheckedData?.length === 0) {
    setActionMenuList([]);
  }
  if (dataList?.length === 0 || selectedModuleCheckedData?.length !== dataList?.length) {
    setIsAllSelected(false);
  } else {
    setIsAllSelected(true);
  }
};

export const generateConfirmationMessage = (moduleName, length, isActive) => {
  const textPluralSuffix = length > 1 ? 's' : '';
  const actionVerb = isActive ? 'activated' : 'deactivated';

  return `${moduleName}${textPluralSuffix} ${actionVerb} successfully.`;
};

export const generatePublishedMessage = (moduleName, length, isActive) => {
  const textPluralSuffix = length > 1 ? 's' : '';
  const actionVerb = isActive ? 'published' : 'drafted';

  return `${moduleName}${textPluralSuffix} ${actionVerb} successfully.`;
};

export const danger = 'danger';
export const success = 'success';

export const encryptData = (data, secretKey) => {
  return CryptoJS.AES.encrypt(data, secretKey).toString();
};

export const decryptData = (encryptedData, secretKey) => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
  return bytes.toString(CryptoJS.enc.Utf8);
};

export const selectedCheckList = (prevList, id) => {
  return prevList?.some((item) => item.id === id && item.isChecked);
};

export const PARTIAL_CHECKBOX_GREEN = 'indeterminate-checkbox-green';

export const ResetFilterList = (prevFilters) =>
  prevFilters?.map((filter) => ({ ...filter, isChecked: false }));
export const formatPrice = (price) => {
  const priceValue = parseFloat(price);
  return priceValue?.toFixed(2);
};

export const validateInteger = (value) => {
  const regex = /^-?\d+$/;
  return regex?.test(value);
};

export const VALID_INTEGER = 'validInteger';
export const handleSearchData = (e, setSearchQuery, setFilteredItems, filteredItemList) => {
  const query = e?.target?.value;
  setSearchQuery(query);
  if (query?.trim() === '') {
    setFilteredItems(filteredItemList);
  } else {
    const filtered = filteredItemList?.filter((item) =>
      item?.name?.toLowerCase()?.includes(query?.trim()?.toLowerCase()),
    );
    setFilteredItems(filtered);
  }
};

export const isDragDisabled = (list, searchText) => list?.length <= 1 || !!searchText;

export const stripHtmlTags = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/<[^>]*>/g, '').trim();
};
